import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from '../api/client';

interface Product {
  id: number;
  sku: string;
  name: string;
  selling_price?: number;
  has_expiry?: boolean;
  cost_price?: number;
  category_name?: string;
  stock?: { location: string; qty: number }[];
}

interface Props {
  value: string;
  onSelect: (product: Product) => void;
  locationId?: string;
  initialLabel?: string;
  fontSize?: number;
}

const ProductPicker: React.FC<Props> = ({ value, onSelect, locationId, initialLabel, fontSize = 12 }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(initialLabel || '');
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (!value) { setSelectedLabel(''); }
  }, [value]);

  useEffect(() => {
    if (initialLabel) setSelectedLabel(initialLabel);
  }, [initialLabel]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (query.trim().length === 0) { setResults([]); setHighlight(-1); return; }
      const params: any = { search: query, limit: 2000, include_zero: 1 };
      if (locationId) params.location_id = locationId;
      Promise.all([
        api.get('/inventory', { params }).catch(() => ({ data: { data: [] } })),
        api.get('/products', { params: { search: query, limit: 200 } }).catch(() => ({ data: { data: [] } })),
      ]).then(([invRes, prodRes]: any[]) => {
        const byProduct: Record<string, any> = {};
        (invRes.data.data || []).forEach((row: any) => {
          const k = String(row.product_id);
          if (!byProduct[k]) {
            byProduct[k] = {
              id: row.product_id, sku: row.sku, name: row.product_name,
              category_name: row.category_name, selling_price: row.selling_price, has_expiry: row.has_expiry, cost_price: row.cost_price,
              stock: [],
            };
          }
          byProduct[k].stock.push({ location: row.location_name, qty: Number(row.qty_available || 0) });
        });
        (prodRes.data.data || []).forEach((p: any) => {
          const k = String(p.id);
          if (!byProduct[k]) {
            byProduct[k] = {
              id: p.id, sku: p.sku, name: p.name,
              category_name: p.category_name, selling_price: p.selling_price, has_expiry: p.has_expiry,
              stock: [],
            };
          }
        });
        setResults(Object.values(byProduct).slice(0, 100) as any);
        setHighlight(-1);
      }).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, locationId]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        inputRef.current && !inputRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (highlight >= 0 && itemRefs.current[highlight]) {
      itemRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlight]);

  const openDropdown = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
    setOpen(true);
    setQuery('');
  };

  const handleSelect = (p: Product) => {
    setSelectedLabel(p.name + ' (' + p.sku + ')');
    setQuery('');
    setOpen(false);
    setHighlight(-1);
    onSelect(p);
  };

  /** A scanner types the code then presses Enter — look it up and take the product straight away */
  const lookupBarcode = async (code: string) => {
    try {
      const r = await api.get('/products/by-barcode', { params: { barcode: code } });
      if (r.data?.data) handleSelect(r.data.data as any);
    } catch {
      // not a barcode — leave the typed text as a search
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') { openDropdown(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight >= 0 && results[highlight]) { handleSelect(results[highlight]); }
      else if (query.trim()) { lookupBarcode(query.trim()); }
    } else if (e.key === 'Escape') {
      setOpen(false);
      setHighlight(-1);
    }
  };

  return (
    <div style={{ position: 'relative', minWidth: 180 }}>
      <input
        ref={inputRef}
        className="form-control"
        style={{ fontSize }}
        placeholder="Search product..."
        value={open ? query : selectedLabel}
        onFocus={openDropdown}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      {open && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999,
            background: '#ffffff', border: '1px solid var(--bdr)', borderRadius: 6,
            maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', marginTop: 2
          }}
        >
          {query.trim().length === 0 ? (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--tx3)' }}>Type to search products...</div>
          ) : results.length === 0 ? (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--tx3)' }}>No matches</div>
          ) : results.map((p, idx) => (
            <div
              key={p.id}
              ref={el => { itemRefs.current[idx] = el; }}
              onClick={() => handleSelect(p)}
              onMouseEnter={() => setHighlight(idx)}
              style={{
                padding: '8px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--bdr)',
                background: idx === highlight ? '#e8f0fe' : '#ffffff'
              }}
            >
              <div style={{ fontWeight: 600 }}>{p.sku} · {p.name}</div>
              <div style={{ color: 'var(--tx3)', fontSize: 11 }}>
                {p.category_name ? p.category_name + '  ·  ' : ''}
                {(p.stock && p.stock.length)
                  ? p.stock.filter((s:any) => s.qty !== 0).slice(0,3).map((s:any) => s.location + ': ' + s.qty).join(', ') || 'No stock'
                  : 'No stock'}
                {p.stock && p.stock.filter((s:any) => s.qty !== 0).length > 3 ? ' +' + (p.stock.filter((s:any) => s.qty !== 0).length - 3) + ' more' : ''}
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ProductPicker;
